import Header from "@components/header/Header";
import PDFViewer from "@components/pdfviewer/PDFViewer";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import styles from "./EvaluationPage.module.css";
import backIcon from "@assets/icons/back-arrow.svg";
import { setComponentToShow } from "@store/ModalToShow";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import QuestionRubricSolution from "@components/questionrubricsolution/QuestionRubricSolution";
import {
  EvaluationContainer,
  EvaluationFooter,
  QuestionsCarousel,
} from "@components/evaluation";
import type { Question } from "@services/evaluationService";
import AssignNextTaskConfirmation from "@modals/assignnexttaskconfirmation/AssignNextTaskConfirmation";
import { toast } from "sonner";

// Server State Hooks
import {
  useAssignEvaluationTask,
  useAssignedEvaluationTask,
  useEvaluationMutations,
} from "@hooks/evaluation";
import type { AttemptStatus } from "@constants/evaluationConstants";

function EvaluationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Get evaluation_group_id from URL query parameters
  const evaluationGroupIdFromUrl = searchParams.get("evaluation_group_id") ?? undefined;

  // Determine mode from router state
  const routerState = location.state as
    | { mode: "resume"; assignmentId: string; forceNewTask?: boolean }
    | { mode: "assign"; evaluationGroupId?: string; forceNewTask?: boolean }
    | null
    | undefined;

  // Use appropriate hook based on mode
  const isResumeMode = routerState?.mode === "resume" && !routerState?.forceNewTask;
  const isAssignMode = routerState?.mode === "assign" || routerState?.forceNewTask;
  const isDirectNavigation = !routerState; // No router state = direct navigation

  // Always call both hooks, but disable the one not in use
  // For resume mode OR direct navigation (fallback), use assigned task hook
  const {
    task: assignedTask,
    questions: assignedQuestions,
    isLoading: isAssignedTaskLoading,
  } = useAssignedEvaluationTask(isResumeMode || isDirectNavigation);

  // For assign mode, use assign task hook with groupId
  const {
    task: assignedNewTask,
    questions: assignedNewQuestions,
    isLoading: isAssignTaskLoading,
    refetch: refetchAssignTask,
  } = useAssignEvaluationTask(
    isAssignMode ? evaluationGroupIdFromUrl : undefined,
    isAssignMode
  );

  // Determine which task and questions to use
  // If direct navigation and assigned task exists, use it (fallback to resume mode)
  const effectiveMode =
    isDirectNavigation && assignedTask
      ? "resume"
      : isResumeMode
      ? "resume"
      : "assign";
  const task = effectiveMode === "resume" ? assignedTask : assignedNewTask;
  const questions =
    effectiveMode === "resume" ? assignedQuestions : assignedNewQuestions;
  const isLoading =
    effectiveMode === "resume" ? isAssignedTaskLoading : isAssignTaskLoading;

  const { saveEvaluation, isSaving, submitEvaluation, isSubmitting } =
    useEvaluationMutations();

  // Redirect to dashboard if no valid mode is provided AND no assigned task found
  useEffect(() => {
    if (
      isDirectNavigation &&
      !isAssignedTaskLoading &&
      !assignedTask &&
      !isLoading
    ) {
      navigate("/dashboard", {
        state: {
          message: "Please select an evaluation task from the dashboard.",
        },
      });
    }
  }, [
    isDirectNavigation,
    isAssignedTaskLoading,
    assignedTask,
    isLoading,
    navigate,
  ]);

  // Redirect if no task is available after loading completes
  useEffect(() => {
    if (!isLoading && (!task || !questions.length)) {
      navigate("/dashboard", {
        state: {
          message:
            "No evaluation task found. Please select a task from the dashboard.",
        },
      });
    }
  }, [isLoading, task, questions.length, navigate]);

  const [questionIndex, setQuestionIndex] = useState(0);

  useEffect(() => {
    setQuestionIndex(0);
    setEvaluationData(null);
  }, [task?.id]);

  // Derived state
  const currentQuestion = questions[questionIndex];
  const solutionPdf = assignedTask
    ? assignedTask.submission_url
    : assignedNewTask
    ? assignedNewTask.submission_url
    : null;

  const [evaluationData, setEvaluationData] = useState<{
    awarded_marks: number;
    feedbacks: string[];
    attempt_status: AttemptStatus;
  } | null>(null);

  const [approvalValid, setApprovalValid] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "question" | "rubric" | "solution" | ""
  >("question");
  const token = localStorage.getItem("accessToken") || "";

  useEffect(() => {
    if (!currentQuestion) return;

    // Reset approval validity when question changes
    const question = currentQuestion;

    // Approval is only valid if:
    // 1. It is L1Evaluated AND has NO student comment AND has a current_evaluation (meaning it was accepted or overridden)
    // 2. OR it is StudentChallenged AND has current_evaluation (meaning it was resolved)
    // Simplified: It must have a current_evaluation to be valid (since L1 acceptance now sets current_evaluation)
    setApprovalValid(question.current_evaluation !== null);

    // Reset evaluationData when question changes - EvaluationContainer will populate it
    // if current_evaluation exists, or when user makes changes
    setEvaluationData(null);
  }, [currentQuestion]);

  const componentName = useSelector(
    (state: any) => state.modalToShow.componentName
  );

  const handleCloseModal = () => {
    dispatch(setComponentToShow(""));
  };

  const renderModal = () => {
    switch (componentName) {
      case "AssignNextTaskConfirmation":
        return (
          <AssignNextTaskConfirmation
            isOpen={true}
            onAssignNextTask={handleAssignNextTask}
            onSubmitAndGoBack={handleSubmitAndGoBack}
            onClose={handleCloseModal}
          />
        );
      default:
        return null;
    }
  };

  const handleQuestionSelect = (index: number) => {
    if (index < 0 || index >= questions.length) return; // boundary check
    setQuestionIndex(index);
  };

  const goToPrevQuestion = () => {
    handleQuestionSelect(questionIndex - 1);
  };

  const goToNextQuestion = () => {
    handleQuestionSelect(questionIndex + 1);
  };

  const proceedWithSubmission = async () => {
    if (!currentQuestion || !evaluationData || !task) return false;

    try {
      await saveEvaluation(task.id, {
        question_id: currentQuestion.question_id,
        awarded_marks: evaluationData.awarded_marks,
        feedbacks: evaluationData.feedbacks,
        attempt_status: evaluationData.attempt_status,
      });
      return true;
    } catch (error) {
      // Error is already handled in the mutation hook with toast
      return false;
    }
  };

  const proceedWithSubmitEvaluation = async (assignmentId: string) => {
    try {
      await submitEvaluation(assignmentId);
      return true;
    } catch (error) {
      // Error is already handled in the mutation hook with toast
      return false;
    }
  };

  const goBackToDashboard = () => {
    navigate("/dashboard");
  };

  const handleApproveAndNext = async () => {
    if (!currentQuestion) return;

    // If evaluationData exists, user has new/changed data to save
    // (accepted L1, overrode existing evaluation, or responded to challenge)
    if (evaluationData) {
      const success = await proceedWithSubmission();
      if (success) {
        goToNextQuestion();
      }
      return;
    }

    // If evaluationData is null but current_evaluation exists,
    // user is viewing already saved evaluation - just navigate
    if (currentQuestion.current_evaluation) {
      goToNextQuestion();
      return;
    }

    // evaluationData is null AND no current_evaluation - incomplete evaluation
    toast.error("Please complete the evaluation before proceeding.");
  };

  const handleApproveAndSubmit = () => {
    if (!currentQuestion || !task) return;

    // Check if all previous questions have been evaluated
    const previousQuestions = questions.slice(0, questionIndex);
    const unevaluatedQuestions = previousQuestions.filter(
      (q) => q.current_evaluation === null
    );

    if (unevaluatedQuestions.length > 0) {
      toast.info(
        "Please complete evaluation of all previous remaining questions before submitting.",
        {
          duration: 5 * 1000, // 10 seconds
        }
      );
      return;
    }

    dispatch(setComponentToShow("AssignNextTaskConfirmation"));
  };

  const handleSubmitAndGoBack = async () => {
    if (!currentQuestion || !task) return;

    dispatch(setComponentToShow(""));

    // If evaluationData exists, save it first (new/changed evaluation)
    if (evaluationData) {
      const saveSuccess = await proceedWithSubmission();
      if (!saveSuccess) {
        return; // Don't proceed if save failed
      }
    } else if (!currentQuestion.current_evaluation) {
      // evaluationData is null AND no current_evaluation - incomplete evaluation
      toast.error("Please complete the evaluation before submitting.");
      return;
    }
    // else: evaluationData is null but current_evaluation exists - already saved, proceed

    // Then submit the entire evaluation
    const submitSuccess = await proceedWithSubmitEvaluation(task.id);
    if (!submitSuccess) {
      return; // Don't proceed if submit failed
    }

    navigate("/dashboard");
  };

  const handleAssignNextTask = async () => {
    if (!currentQuestion || !task) return;

    dispatch(setComponentToShow(""));

    // If evaluationData exists, save it first (new/changed evaluation)
    if (evaluationData) {
      const saveSuccess = await proceedWithSubmission();
      if (!saveSuccess) {
        return; // Don't proceed if save failed
      }
    } else if (!currentQuestion.current_evaluation) {
      // evaluationData is null AND no current_evaluation - incomplete evaluation
      toast.error("Please complete the evaluation before submitting.");
      return;
    }
    // else: evaluationData is null but current_evaluation exists - already saved, proceed

    // Then submit the entire evaluation
    const submitSuccess = await proceedWithSubmitEvaluation(task.id);
    if (!submitSuccess) {
      return; // Don't proceed if submit failed
    }

    const groupId = evaluationGroupIdFromUrl || task.evaluation_group_id;
    if (isAssignMode && refetchAssignTask) {
      try {
        const result = await refetchAssignTask();
        if (!result.data) {
          navigate("/dashboard", {
            state: {
              message: "No more evaluation tasks available.",
            },
          });
        }
      } catch {
        navigate("/dashboard");
      }
    } else {
      navigate(`/evaluation?evaluation_group_id=${groupId}`, {
        state: {
          mode: "assign",
          evaluationGroupId: groupId,
          forceNewTask: true,
        },
      });
    }
  };

  if (isLoading) {
    return <div className={styles.loadingContainer}>Loading...</div>;
  }

  // Show loading state while redirecting
  if (!task || !questions.length) {
    return <div className={styles.loadingContainer}>No task available...</div>;
  }

  return (
    <>
      <Header />
      <div className={styles.evaluatorPage}>
        <div className={styles.evaluatorHeader}>
          <button className={styles.backBtn} onClick={goBackToDashboard}>
            <img src={backIcon} /> Back to Dashboard
          </button>
          <div className={styles.testInfo}>
            {/* <h2>Maths Grade-9 Unit-1 Test - 15th May 2025</h2> */}
            {/* <p>Admission ID:{"N/A"}</p> */}
          </div>
        </div>
        <div className={styles.mainContainer}>
          <QuestionsCarousel
            questions={questions}
            questionIndex={questionIndex}
            onQuestionSelect={handleQuestionSelect}
          />

          <div className={styles.evaluatorMainContent}>
            <div className={styles.leftSidePanel}>
              <div className={styles.tabBar}>
                <button
                  className={styles.tab}
                  onClick={() =>
                    setActiveTab((prev) =>
                      prev === "question" ? "" : "question"
                    )
                  }
                >
                  <span className={styles.tabLabel}>View Question</span>
                  {activeTab === "question" && (
                    <motion.div
                      layoutId="activeTab"
                      className={styles.activeTabIndicator}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                </button>
                <button
                  className={styles.tab}
                  onClick={() =>
                    setActiveTab((prev) => (prev === "rubric" ? "" : "rubric"))
                  }
                >
                  <span className={styles.tabLabel}>View Rubric</span>
                  {activeTab === "rubric" && (
                    <motion.div
                      layoutId="activeTab"
                      className={styles.activeTabIndicator}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                </button>
                <button
                  className={styles.tab}
                  onClick={() =>
                    setActiveTab((prev) =>
                      prev === "solution" ? "" : "solution"
                    )
                  }
                >
                  <span className={styles.tabLabel}>View Solution</span>
                  {activeTab === "solution" && (
                    <motion.div
                      layoutId="activeTab"
                      className={styles.activeTabIndicator}
                      transition={{
                        type: "spring",
                        bounce: 0.2,
                        duration: 0.6,
                      }}
                    />
                  )}
                </button>
              </div>
              <AnimatePresence mode="wait">
                {activeTab && (
                  <motion.div
                    key={`${questionIndex}-${activeTab}`}
                    className={styles.slideContainer}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{
                      height: "auto",
                      opacity: 1,
                    }}
                    exit={{
                      height: 0,
                      opacity: 0,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 25,
                      mass: 0.8,
                    }}
                  >
                    <p className={styles.sectionTitle}>
                      {activeTab === "question"
                        ? `Question`
                        : activeTab === "rubric"
                        ? "Rubric"
                        : "Solution"}
                    </p>
                    <div className={styles.questionSection}>
                      {currentQuestion && (
                        <QuestionRubricSolution
                          activeTab={activeTab}
                          question={currentQuestion}
                          token={token}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <p>Student Answer</p>
              <div className={styles.studentPdfSection}>
                {solutionPdf && (
                  <PDFViewer pdfUrl={solutionPdf} title="PDF Answer Sheet" />
                )}
              </div>
            </div>
            <div className={styles.rightSidePanel}>
              {currentQuestion && (
                <EvaluationContainer
                  question={currentQuestion as Question}
                  onApprovalValidityChange={setApprovalValid}
                  onEvaluationChange={setEvaluationData}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <EvaluationFooter
        questionIndex={questionIndex}
        totalQuestions={questions.length}
        approvalValid={approvalValid}
        isSaving={isSaving || isSubmitting}
        onPrevious={goToPrevQuestion}
        onApproveAndSubmit={handleApproveAndSubmit}
        onApproveAndNext={handleApproveAndNext}
      />
      {renderModal()}
    </>
  );
}

export default EvaluationPage;
